/** @typedef {import('./types').HostKind} HostKind */
/** @typedef {import('./types').MailItemSnapshot} MailItemSnapshot */

import {
  getEmailBody,
  setItemBody,
  getBodyType,
  getEmailSubject,
  getRecepients,
  getSender,
  getCC,
} from "../taskpane/utils/outlookUtils";
import { describeMailboxItem } from "../taskpane/utils/outlookLoadDebug";

/**
 * @param {unknown} recipients
 * @returns {{ emailAddress: string, displayName: string }[]}
 */
const mapRecipients = (recipients) => {
  if (!Array.isArray(recipients)) {
    // Compose mode: item.to / item.cc are Recipients objects with getAsync, not arrays
    return [];
  }
  return recipients.map((r) => ({
    emailAddress: r?.emailAddress || "",
    displayName: r?.displayName || "",
  }));
};

/**
 * @param {unknown} address
 * @returns {{ emailAddress: string, displayName: string } | undefined}
 */
const mapAddress = (address) => {
  if (!address || typeof address !== "object") {
    return undefined;
  }
  // Compose mode: from/sender may be async objects without emailAddress
  if (typeof address.emailAddress !== "string" && typeof address.displayName !== "string") {
    return undefined;
  }
  return {
    emailAddress: address.emailAddress || "",
    displayName: address.displayName || "",
  };
};

/**
 * @param {Office.MessageCompose | Office.MessageRead | undefined} item
 * @returns {MailItemSnapshot | null}
 */
export const snapshotFromOfficeItem = (item) => {
  if (!item) {
    return null;
  }

  const isCompose = typeof item.subject === "object" && typeof item.subject?.setAsync === "function";

  const readSubject = typeof item.subject === "string" ? item.subject : undefined;

  return {
    from: mapAddress(item.from),
    to: mapRecipients(item.to),
    cc: mapRecipients(item.cc),
    sender: mapAddress(item.sender),
    subject: readSubject,
    internetMessageId: typeof item.internetMessageId === "string" ? item.internetMessageId : undefined,
    inReplyTo: typeof item.inReplyTo === "string" ? item.inReplyTo : undefined,
    conversationId: typeof item.conversationId === "string" ? item.conversationId : undefined,
    entryId: typeof item.itemId === "string" ? item.itemId : undefined,
    itemType: item.itemType,
    isCompose,
  };
};

/**
 * @returns {Office.MessageCompose | Office.MessageRead | undefined}
 */
const getOfficeItem = () => Office.context?.mailbox?.item;

/**
 * @returns {Promise<void>}
 */
const waitForOfficeJs = () => {
  if (typeof Office !== "undefined") {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="office.js"]');
    const onLoaded = () => {
      if (typeof Office !== "undefined") {
        resolve();
      } else {
        reject(new Error("Office.js loaded but Office global is unavailable"));
      }
    };

    if (existing) {
      existing.addEventListener("load", onLoaded, { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Office.js")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://appsforoffice.microsoft.com/lib/1.1/hosted/office.js";
    script.onload = onLoaded;
    script.onerror = () => reject(new Error("Failed to load Office.js"));
    document.head.appendChild(script);
  });
};

/**
 * @returns {import('./officeJsHost').OutlookHost}
 */
export const createOfficeJsHost = () => {
  /** @type {Set<(snapshot: MailItemSnapshot) => void>} */
  const itemChangedListeners = new Set();
  let officeItemChangedHandler = null;

  const notifyItemChanged = () => {
    const snapshot = snapshotFromOfficeItem(getOfficeItem());
    if (snapshot) {
      itemChangedListeners.forEach((listener) => listener(snapshot));
    }
  };

  return {
    kind: /** @type {HostKind} */ ("office-js"),

    async onReady() {
      await waitForOfficeJs();
      await new Promise((resolve) => {
        Office.onReady(() => resolve());
      });
      officeItemChangedHandler = notifyItemChanged;
      Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, officeItemChangedHandler);
    },

    onItemChanged(handler) {
      itemChangedListeners.add(handler);
      return () => {
        itemChangedListeners.delete(handler);
      };
    },

    async waitForItemReady() {
      return new Promise((resolve, reject) => {
        const pollInterval = setInterval(() => {
          const item = getOfficeItem();
          if (!item) {
            return;
          }

          if (typeof item.subject === "string") {
            clearInterval(pollInterval);
            resolve(snapshotFromOfficeItem(item));
            return;
          }

          if (item.subject?.getAsync) {
            item.subject.getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                clearInterval(pollInterval);
                const snapshot = snapshotFromOfficeItem(item);
                if (snapshot) {
                  snapshot.subject = result.value;
                }
                resolve(snapshot);
              }
            });
          }
        }, 1000);

        setTimeout(() => {
          clearInterval(pollInterval);
          const item = getOfficeItem();
          if (item) {
            resolve(snapshotFromOfficeItem(item));
          } else {
            reject(new Error("Subject did not become available in time."));
          }
        }, 15000);
      });
    },

    getSnapshot() {
      return snapshotFromOfficeItem(getOfficeItem());
    },

    getOfficeItem() {
      return getOfficeItem();
    },

    isComposeMode() {
      const item = getOfficeItem();
      return typeof item?.subject === "object" && typeof item?.subject?.setAsync === "function";
    },

    getUserDisplayName() {
      return Office.context.mailbox.userProfile?.displayName || "";
    },

    getMessageKey() {
      const item = getOfficeItem();
      return item?.internetMessageId || item?.inReplyTo || "";
    },

    async getSubject() {
      const item = getOfficeItem();
      if (!item) {
        return "";
      }
      if (typeof item.subject === "string") {
        return item.subject;
      }
      return getEmailSubject(item);
    },

    async setSubject(subject) {
      const item = getOfficeItem();
      if (item?.subject?.setAsync) {
        return new Promise((resolve, reject) => {
          item.subject.setAsync(subject, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve();
            } else {
              reject(new Error(result.error?.message || "Failed to set subject"));
            }
          });
        });
      }
    },

    async getBodyText(caller = "host") {
      const item = getOfficeItem();
      const result = await getEmailBody(item, Office.CoercionType.Text, caller);
      return result?.value || "";
    },

    async getBodyHtml(caller = "host") {
      const item = getOfficeItem();
      const result = await getEmailBody(item, Office.CoercionType.Html, caller);
      return result?.value || "";
    },

    async getBodyType(caller = "host") {
      const item = getOfficeItem();
      const coercionType = await getBodyType(item, caller);
      return coercionType === Office.CoercionType.Html ? "html" : "text";
    },

    async setBody(content) {
      const item = getOfficeItem();
      await setItemBody(item, content);
    },

    async getSelectedText() {
      const item = getOfficeItem();
      if (!item?.getSelectedDataAsync) {
        return { data: "" };
      }
      return new Promise((resolve) => {
        item.getSelectedDataAsync(Office.CoercionType.Text, (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            resolve(asyncResult.value);
          } else {
            resolve({ data: "" });
          }
        });
      });
    },

    async getRecipients() {
      const item = getOfficeItem();
      if (this.isComposeMode()) {
        const [toResult, fromResult, ccResult] = await Promise.all([
          getRecepients(item),
          getSender(item),
          getCC(item),
        ]);
        return {
          to: toResult.value || [],
          sender: fromResult.value,
          cc: ccResult.value || [],
        };
      }
      return {
        to: item?.to || [],
        sender: item?.from,
        cc: item?.cc || [],
      };
    },

    async attachFileFromUrl(url, filename) {
      const item = getOfficeItem();
      if (!this.isComposeMode() || !item?.addFileAttachmentAsync) {
        throw new Error("Attachments are only supported in compose mode");
      }
      return new Promise((resolve, reject) => {
        item.addFileAttachmentAsync(url, filename, { isInline: false }, (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(asyncResult.error);
          }
        });
      });
    },

    openReplyForm(content) {
      const item = getOfficeItem();
      item?.displayReplyForm?.(content);
    },

    openReplyAllForm(content) {
      const item = getOfficeItem();
      item?.displayReplyAllForm?.(content);
    },

    async getConversationThread() {
      return [];
    },

    isMessageItem() {
      const item = getOfficeItem();
      return item?.itemType === Office.MailboxEnums.ItemType.Message;
    },

    supportsGraph() {
      return true;
    },

    describeCurrentItem() {
      return describeMailboxItem(getOfficeItem());
    },

    dispose() {
      if (officeItemChangedHandler) {
        try {
          Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, officeItemChangedHandler);
        } catch {
          // ignore cleanup errors
        }
        officeItemChangedHandler = null;
      }
      itemChangedListeners.clear();
    },
  };
};

/** @typedef {ReturnType<typeof createOfficeJsHost>} OutlookHost */
